<template>
    <div class="arms-page">
        <PageSpinner :onloader="onloader" />
        <base-header type="gradient-info" class="pb-6 pb-8 pt-5 pt-md-8">
            <router-link to="/dashboard" class="text-white">
                Dashboard
            </router-link>
            <router-link to="/school/academic-management" class="text-white">
                <i class="fa fa-ellipsis-v px-2"></i>Academic Management
            </router-link>
            <span class="text-white"><i class="fa fa-ellipsis-v px-2"></i>Arms</span>
        </base-header>

        <div class="container-fluid mt--7 arms-page__body">
            <section class="arms-panel" aria-labelledby="arms-page-title">
                <header class="arms-panel__intro">
                    <div class="arms-panel__text">
                        <span class="arms-eyebrow">ACADEMIC STRUCTURE</span>
                        <h1 id="arms-page-title">Class arms</h1>
                        <p>
                            Arms split a class option into teaching groups. Register the arms
                            available for each class and option.
                        </p>
                    </div>

                    <div class="arms-panel__side">
                        <div
                            v-if="selectedOptionId && armitems !== null"
                            class="arms-stat"
                        >
                            <strong>{{ arms.length }}</strong>
                            <span>{{ arms.length === 1 ? "arm" : "arms" }}</span>
                        </div>
                        <button
                            type="button"
                            class="btn arms-add"
                            :disabled="!classes.length"
                            :title="classes.length ? 'Register a new arm' : 'Create a class first'"
                            @click="openCreate"
                        >
                            <i class="fa fa-plus" aria-hidden="true"></i>
                            <span>New arm</span>
                        </button>
                    </div>
                </header>

                <header class="arms-toolbar">
                    <div class="arms-toolbar__title">
                        <h2>Registered arms</h2>
                        <p class="arms-toolbar__meta">{{ scopeLabel }}</p>
                    </div>

                    <div class="arms-toolbar__tools">
                        <label class="arms-scope">
                            <span>Class</span>
                            <select
                                v-model="selectedClassId"
                                class="arms-scope__select"
                                :disabled="classesLoading || !classes.length"
                                aria-label="Class to show arms for"
                                @change="onClassChange"
                            >
                                <option v-if="classesLoading" :value="0" disabled>
                                    Loading classes…
                                </option>
                                <option v-else :value="0">Select class</option>
                                <option v-for="item in classes" :key="item.Id" :value="item.Id">
                                    {{ item.Sch_Class }}
                                </option>
                            </select>
                        </label>

                        <label class="arms-scope">
                            <span>Option</span>
                            <select
                                v-model="selectedOptionId"
                                class="arms-scope__select"
                                :disabled="!selectedClassId || optionsLoading || !options.length"
                                aria-label="Option to show arms for"
                                @change="onOptionChange"
                            >
                                <option v-if="!selectedClassId" :value="0" disabled>
                                    Select class first
                                </option>
                                <option v-else-if="optionsLoading" :value="0" disabled>
                                    Loading options…
                                </option>
                                <option v-else-if="!options.length" :value="0" disabled>
                                    No options
                                </option>
                                <option v-else :value="0">Select option</option>
                                <option v-for="item in options" :key="item.Id" :value="item.Id">
                                    {{ item.ClassOption }}
                                </option>
                            </select>
                        </label>

                        <div class="arms-search">
                            <i class="fa fa-search" aria-hidden="true"></i>
                            <input
                                v-model="searchval"
                                type="search"
                                class="arms-search__input"
                                placeholder="Search arms"
                                aria-label="Search arms for the selected option"
                                :disabled="!arms.length"
                            />
                            <button
                                v-if="searchval"
                                type="button"
                                class="arms-search__clear"
                                aria-label="Clear search"
                                @click="searchval = ''"
                            >
                                <i class="fa fa-times" aria-hidden="true"></i>
                            </button>
                        </div>
                    </div>
                </header>

                <div v-if="classesLoading" class="arms-state">
                    <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                    <strong>Loading classes</strong>
                    <span>Fetching the classes available at your school.</span>
                </div>

                <div v-else-if="classesFailed" class="arms-state">
                    <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                    <strong>Classes could not be loaded</strong>
                    <span>Check your connection and try again.</span>
                    <button type="button" class="btn arms-ghost-button" @click="retryClasses">
                        <i class="fa fa-refresh" aria-hidden="true"></i>
                        <span>Try again</span>
                    </button>
                </div>

                <div v-else-if="!classes.length" class="arms-state">
                    <i class="fa fa-graduation-cap" aria-hidden="true"></i>
                    <strong>No classes available</strong>
                    <span>Create classes before registering arms for them.</span>
                    <router-link
                        to="/school/academic-management/classes"
                        class="btn arms-ghost-button"
                    >
                        <i class="fa fa-arrow-right" aria-hidden="true"></i>
                        <span>Go to classes</span>
                    </router-link>
                </div>

                <div v-else-if="!selectedClassId" class="arms-state">
                    <i class="fa fa-filter" aria-hidden="true"></i>
                    <strong>Select a class</strong>
                    <span>Choose a class above to see and manage its arms.</span>
                </div>

                <div v-else-if="optionsLoading && optionitems === null" class="arms-state">
                    <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                    <strong>Loading options</strong>
                    <span>Fetching the options registered for {{ selectedClassName }}.</span>
                </div>

                <div v-else-if="optionsFailed" class="arms-state">
                    <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                    <strong>Options could not be loaded</strong>
                    <span>Try loading the options for {{ selectedClassName }} again.</span>
                    <button type="button" class="btn arms-ghost-button" @click="retryOptions">
                        <i class="fa fa-refresh" aria-hidden="true"></i>
                        <span>Try again</span>
                    </button>
                </div>

                <div v-else-if="!options.length" class="arms-state">
                    <i class="fa fa-sitemap" aria-hidden="true"></i>
                    <strong>{{ selectedClassName }} has no options</strong>
                    <span>Arms belong to an option. Create an option for this class first.</span>
                    <router-link
                        to="/school/academic-management/options"
                        class="btn arms-ghost-button"
                    >
                        <i class="fa fa-arrow-right" aria-hidden="true"></i>
                        <span>Go to options</span>
                    </router-link>
                </div>

                <div v-else-if="!selectedOptionId" class="arms-state">
                    <i class="fa fa-filter" aria-hidden="true"></i>
                    <strong>Select an option</strong>
                    <span>Choose an option above to see the arms registered under it.</span>
                </div>

                <div v-else-if="armsLoading && armitems === null" class="arms-state">
                    <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                    <strong>Loading arms</strong>
                    <span>Fetching arms for {{ selectedClassName }} · {{ selectedOptionName }}.</span>
                </div>

                <div v-else-if="armsFailed" class="arms-state">
                    <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                    <strong>Arms could not be loaded</strong>
                    <span>Try loading the arms for {{ selectedOptionName }} again.</span>
                    <button type="button" class="btn arms-ghost-button" @click="retryArms">
                        <i class="fa fa-refresh" aria-hidden="true"></i>
                        <span>Try again</span>
                    </button>
                </div>

                <div v-else-if="!arms.length" class="arms-state">
                    <i class="fa fa-list-ul" aria-hidden="true"></i>
                    <strong>No arms for {{ selectedOptionName }}</strong>
                    <span>Register an arm such as A, B or Gold.</span>
                    <button type="button" class="btn arms-ghost-button" @click="openCreate">
                        <i class="fa fa-plus" aria-hidden="true"></i>
                        <span>Add arm</span>
                    </button>
                </div>

                <div v-else-if="!filteredArms.length" class="arms-state">
                    <i class="fa fa-search" aria-hidden="true"></i>
                    <strong>No arms match “{{ searchval.trim() }}”</strong>
                    <span>Try a different arm name.</span>
                    <button type="button" class="btn arms-ghost-button" @click="searchval = ''">
                        Clear search
                    </button>
                </div>

                <div v-else class="arms-grid" :class="{ 'is-loading': armsLoading }">
                    <article
                        v-for="(item, index) in filteredArms"
                        :key="rowKey(item, index)"
                        class="arm-card"
                    >
                        <span class="arm-card__index">{{ String(index + 1).padStart(2, "0") }}</span>
                        <span class="arm-card__name">{{ armName(item) }}</span>
                        <button
                            type="button"
                            class="arm-card__edit"
                            :aria-label="editLabel(item)"
                            @click="editItem(item)"
                        >
                            <i class="fa fa-pencil" aria-hidden="true"></i>
                            <span>Edit</span>
                        </button>
                    </article>
                </div>
            </section>
        </div>

        <modal
            :show.sync="formVisible"
            modal-classes="modal-dialog-centered"
            @close="handleFormClosed"
        >
            <div class="arms-drawer">
                <header class="arms-drawer__head">
                    <span class="arms-drawer__icon" aria-hidden="true">
                        <i class="fa" :class="editingId ? 'fa-pencil' : 'fa-list-ul'"></i>
                    </span>
                    <div class="arms-drawer__heading">
                        <span class="arms-drawer__eyebrow">
                            {{ editingId ? "UPDATE RECORD" : "NEW RECORD" }}
                        </span>
                        <h2>{{ editingId ? "Edit arm" : "Add an arm" }}</h2>
                        <p v-if="drawerLabel">{{ drawerLabel }}</p>
                        <p v-else>Choose a class and option, then name the arm.</p>
                    </div>
                    <button
                        type="button"
                        class="arms-drawer__close"
                        aria-label="Close"
                        @click="closeForm"
                    >
                        <i class="fa fa-times" aria-hidden="true"></i>
                    </button>
                </header>

                <form id="arm-form" class="arms-form" novalidate @submit.prevent="submitArm">
                    <fieldset class="arms-fieldset">
                        <legend>Arm details</legend>

                        <div class="row">
                            <div class="col-sm-6">
                                <div class="form-group">
                                    <label for="arm-class">
                                        Class <span class="arms-required">*</span>
                                    </label>
                                    <select
                                        id="arm-class"
                                        v-model="form.classId"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.classId }"
                                        :disabled="isSaving || !classes.length"
                                        @change="onFormClassChange"
                                    >
                                        <option :value="0">Select class</option>
                                        <option v-for="item in classes" :key="item.Id" :value="item.Id">
                                            {{ item.Sch_Class }}
                                        </option>
                                    </select>
                                    <span v-if="errors.classId" class="arms-error" role="alert">
                                        {{ errors.classId }}
                                    </span>
                                </div>
                            </div>

                            <div class="col-sm-6">
                                <div class="form-group">
                                    <label for="arm-option">
                                        Option <span class="arms-required">*</span>
                                    </label>
                                    <select
                                        id="arm-option"
                                        v-model="form.optionId"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.optionId }"
                                        :disabled="isSaving || !form.classId || formOptionsLoading"
                                        @change="clearError('optionId')"
                                    >
                                        <option v-if="!form.classId" :value="0" disabled>
                                            Select a class first
                                        </option>
                                        <option v-else-if="formOptionsLoading" :value="0" disabled>
                                            Loading options…
                                        </option>
                                        <option v-else-if="!formOptions.length" :value="0" disabled>
                                            No options for this class
                                        </option>
                                        <option v-else :value="0">Select option</option>
                                        <option v-for="item in formOptions" :key="item.Id" :value="item.Id">
                                            {{ item.ClassOption }}
                                        </option>
                                    </select>
                                    <span v-if="errors.optionId" class="arms-error" role="alert">
                                        {{ errors.optionId }}
                                    </span>
                                    <span v-else-if="formOptionsFailed" class="arms-error">
                                        Options could not be loaded.
                                        <button
                                            type="button"
                                            class="arms-inline-button"
                                            @click="loadFormOptions(form.optionId)"
                                        >
                                            Try again
                                        </button>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="form-group">
                            <label for="arm-name">
                                Arm name <span class="arms-required">*</span>
                            </label>
                            <input
                                id="arm-name"
                                v-model="form.armName"
                                type="text"
                                class="form-control"
                                :class="{ 'is-invalid': errors.armName }"
                                placeholder="e.g. A or Gold"
                                maxlength="60"
                                autocomplete="off"
                                :disabled="isSaving"
                                @input="clearError('armName')"
                            />
                            <span v-if="errors.armName" class="arms-error" role="alert">
                                {{ errors.armName }}
                            </span>
                            <span v-else class="arms-hint">
                                Keep it short — this label appears on class lists and result sheets.
                            </span>
                        </div>

                        <div v-if="showFormOptionsNotice" class="arms-notice">
                            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                            <span>
                                This class has no options yet. Create an option before adding
                                arms.
                                <router-link to="/school/academic-management/options">
                                    Go to options
                                </router-link>
                            </span>
                        </div>
                    </fieldset>
                </form>
            </div>

            <template slot="footer">
                <div class="arms-drawer__actions">
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        :disabled="isSaving"
                        @click="closeForm"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="arm-form"
                        class="btn arms-submit"
                        :disabled="isSaving || !formOptions.length"
                    >
                        <i
                            class="fa"
                            :class="isSaving ? 'fa-spinner fa-spin' : 'fa-check'"
                            aria-hidden="true"
                        ></i>
                        <span>{{ submitLabel }}</span>
                    </button>
                </div>
            </template>
        </modal>
    </div>
</template>

<script>
import PageSpinner from "@/components/PageSpinner";
import Alert from "@/mixins/Alert.mixins";
import modal from "@/components/Modal";

const EMPTY_FORM = () => ({ classId: 0, optionId: 0, armName: "" });

export default {
    name: "arm",
    mixins: [Alert],
    components: {
        PageSpinner,
        modal
    },
    data() {
        return {
            accessDetails: null,
            classitems: null,
            optionitems: null,
            armitems: null,
            selectedClassId: 0,
            selectedOptionId: 0,
            searchval: "",
            classesFailed: false,
            optionsFailed: false,
            armsFailed: false,
            optionsLoading: false,
            armsLoading: false,
            optionSeq: 0,
            armSeq: 0,
            formVisible: false,
            isSaving: false,
            editingId: 0,
            form: EMPTY_FORM(),
            formOptions: [],
            formOptionsLoading: false,
            formOptionsFailed: false,
            formOptionSeq: 0,
            errors: {},
            onloader: "none"
        };
    },
    computed: {
        classes() {
            return Array.isArray(this.classitems) ? this.classitems : [];
        },
        options() {
            return Array.isArray(this.optionitems) ? this.optionitems : [];
        },
        arms() {
            return Array.isArray(this.armitems) ? this.armitems : [];
        },
        classesLoading() {
            return this.classitems === null;
        },
        selectedClassName() {
            const selected = this.classes.find(
                item => String(item.Id) === String(this.selectedClassId)
            );
            return selected ? selected.Sch_Class : "";
        },
        selectedOptionName() {
            const selected = this.options.find(
                item => String(item.Id) === String(this.selectedOptionId)
            );
            return selected ? selected.ClassOption : "";
        },
        formClassName() {
            const selected = this.classes.find(
                item => String(item.Id) === String(this.form.classId)
            );
            return selected ? selected.Sch_Class : "";
        },
        formOptionName() {
            const selected = this.formOptions.find(
                item => String(item.Id) === String(this.form.optionId)
            );
            return selected ? selected.ClassOption : "";
        },
        drawerLabel() {
            return [this.formClassName, this.formOptionName].filter(Boolean).join(" · ");
        },
        hasSearch() {
            return this.searchval.trim().length > 0;
        },
        filteredArms() {
            const tokens = this.searchval
                .toLowerCase()
                .split(/\s+/)
                .filter(Boolean);

            if (!tokens.length) return this.arms;

            return this.arms.filter(item => {
                const haystack = [item.SectionName, item.SchArm, item.ArmName]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return tokens.every(token => haystack.includes(token));
            });
        },
        scopeLabel() {
            if (this.classesLoading) return "Loading classes…";
            if (this.classesFailed) return "Classes unavailable.";
            if (!this.classes.length) return "No classes available.";
            if (!this.selectedClassId) return "Choose a class to view its arms.";
            if (this.optionsLoading) return `${this.selectedClassName} · loading options…`;
            if (this.optionsFailed) return `${this.selectedClassName} · options unavailable`;
            if (!this.options.length) return `${this.selectedClassName} · no options`;
            if (!this.selectedOptionId) return `${this.selectedClassName} · choose an option`;
            if (this.armsLoading) {
                return `${this.selectedClassName} · ${this.selectedOptionName} · loading arms…`;
            }
            if (this.armsFailed) {
                return `${this.selectedClassName} · ${this.selectedOptionName} · arms unavailable`;
            }
            if (this.hasSearch) {
                return `${this.selectedClassName} · ${this.selectedOptionName} · ${this.filteredArms.length} of ${this.arms.length} shown`;
            }
            const count = this.arms.length;
            return `${this.selectedClassName} · ${this.selectedOptionName} · ${count} ${count === 1 ? "arm" : "arms"}`;
        },
        showFormOptionsNotice() {
            return (
                Boolean(this.form.classId) &&
                !this.formOptionsLoading &&
                !this.formOptionsFailed &&
                !this.formOptions.length
            );
        },
        submitLabel() {
            if (this.isSaving) return "Saving…";
            return this.editingId ? "Save changes" : "Create arm";
        }
    },
    methods: {
        rowKey(item, index) {
            const id = item && (item.Id || item.id);
            return id !== undefined && id !== null && id !== "" ? id : `arm-${index}`;
        },
        armName(item) {
            if (!item) return "—";
            return item.SectionName || item.SchArm || item.ArmName || "—";
        },
        editLabel(item) {
            const name = this.armName(item);
            return name === "—" ? "Edit arm" : `Edit ${name}`;
        },
        errorMessage(error, fallback) {
            const data = error && error.response && error.response.data;
            if (typeof data === "string" && data.trim()) return data.trim();
            if (data && typeof data === "object") {
                return data.message || data.responseMessage || fallback;
            }
            if (error && error.message) return error.message;
            return fallback;
        },
        async loadClasses() {
            this.classitems = null;
            this.classesFailed = false;

            try {
                const resp = await this.$store.dispatch("getclass");
                this.classitems = Array.isArray(resp) ? resp : [];

                const hasSelection = this.classitems.some(
                    item => String(item.Id) === String(this.selectedClassId)
                );
                if (!hasSelection) {
                    this.selectedClassId = this.classitems.length
                        ? this.classitems[0].Id
                        : 0;
                }

                if (this.selectedClassId) {
                    await this.loadOptions();
                } else {
                    this.optionitems = null;
                    this.armitems = null;
                }
            } catch (error) {
                this.classitems = [];
                this.classesFailed = true;
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load classes at the moment."),
                    ""
                );
            }
        },
        async loadOptions(options = {}) {
            const silent = Boolean(options && options.silent);
            const classId = this.selectedClassId;
            if (!classId) {
                this.optionitems = null;
                this.selectedOptionId = 0;
                this.armitems = null;
                this.optionsLoading = false;
                return;
            }

            const sequence = ++this.optionSeq;
            this.optionsLoading = true;
            this.optionsFailed = false;

            try {
                const resp = await this.$store.dispatch("getoptionbyclass", classId);
                if (sequence !== this.optionSeq) return;
                this.optionitems = Array.isArray(resp) ? resp : [];

                const exists = this.optionitems.some(
                    item => String(item.Id) === String(this.selectedOptionId)
                );
                if (!exists) {
                    this.selectedOptionId = this.optionitems.length
                        ? this.optionitems[0].Id
                        : 0;
                }
            } catch (error) {
                if (sequence !== this.optionSeq) return;
                this.optionsFailed = true;
                this.optionitems = [];
                this.selectedOptionId = 0;
                if (!silent) {
                    this.ShowAlertError(
                        this.errorMessage(error, "Unable to load options for this class."),
                        ""
                    );
                }
            } finally {
                if (sequence === this.optionSeq) this.optionsLoading = false;
            }

            if (sequence !== this.optionSeq) return;
            if (this.selectedOptionId) {
                await this.loadArms({ silent });
            } else {
                this.armitems = null;
            }
        },
        async loadArms(options = {}) {
            const silent = Boolean(options && options.silent);
            const optionId = this.selectedOptionId;
            if (!optionId) {
                this.armitems = null;
                this.armsLoading = false;
                return;
            }

            const sequence = ++this.armSeq;
            this.armsLoading = true;
            this.armsFailed = false;

            try {
                const resp = await this.$store.dispatch("getarm", optionId);
                if (sequence !== this.armSeq) return;
                this.armitems = Array.isArray(resp) ? resp : [];
            } catch (error) {
                if (sequence !== this.armSeq) return;
                this.armsFailed = true;
                this.armitems = [];
                if (!silent) {
                    this.ShowAlertError(
                        this.errorMessage(error, "Unable to load arms for this option."),
                        ""
                    );
                }
            } finally {
                if (sequence === this.armSeq) this.armsLoading = false;
            }
        },
        async loadFormOptions(preferredOptionId) {
            const classId = this.form.classId;
            const sequence = ++this.formOptionSeq;

            if (!classId) {
                this.formOptions = [];
                this.form.optionId = 0;
                this.formOptionsLoading = false;
                return;
            }

            this.formOptionsLoading = true;
            this.formOptionsFailed = false;

            try {
                const resp = await this.$store.dispatch("getoptionbyclass", classId);
                if (sequence !== this.formOptionSeq) return;
                this.formOptions = Array.isArray(resp) ? resp : [];

                const preferred =
                    preferredOptionId === null || preferredOptionId === undefined
                        ? this.form.optionId
                        : preferredOptionId;
                const exists = this.formOptions.some(
                    item => String(item.Id) === String(preferred)
                );
                this.form.optionId = exists
                    ? preferred
                    : this.formOptions.length
                    ? this.formOptions[0].Id
                    : 0;
            } catch (error) {
                if (sequence !== this.formOptionSeq) return;
                this.formOptions = [];
                this.formOptionsFailed = true;
                this.form.optionId = 0;
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load options for this class."),
                    ""
                );
            } finally {
                if (sequence === this.formOptionSeq) this.formOptionsLoading = false;
            }
        },
        retryClasses() {
            this.loadClasses();
        },
        retryOptions() {
            this.optionsFailed = false;
            this.optionitems = null;
            this.selectedOptionId = 0;
            this.armitems = null;
            this.loadOptions();
        },
        retryArms() {
            this.armsFailed = false;
            this.armitems = null;
            this.loadArms();
        },
        onClassChange() {
            this.searchval = "";
            this.selectedOptionId = 0;
            this.optionitems = null;
            this.armitems = null;
            this.optionsFailed = false;
            this.armsFailed = false;
            this.loadOptions();
        },
        onOptionChange() {
            this.searchval = "";
            this.armitems = null;
            this.armsFailed = false;
            this.loadArms();
        },
        onFormClassChange() {
            this.formOptions = [];
            this.form.optionId = 0;
            this.formOptionsFailed = false;
            this.clearError("classId");
            this.clearError("optionId");
            this.loadFormOptions();
        },
        resetForm() {
            this.editingId = 0;
            this.errors = {};
            this.form = EMPTY_FORM();
            this.formOptions = [];
            this.formOptionsLoading = false;
            this.formOptionsFailed = false;
            this.formOptionSeq += 1;
        },
        openCreate() {
            if (!this.classes.length) return;
            this.resetForm();
            const classId = this.selectedClassId || 0;
            this.form = { classId, optionId: 0, armName: "" };
            this.formVisible = true;
            if (classId) {
                this.loadFormOptions(
                    classId === this.selectedClassId ? this.selectedOptionId : 0
                );
            }
        },
        async editItem(item) {
            if (!item) return;
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("getarmbyid", item.Id);
                if (!resp) throw new Error("The selected arm could not be loaded.");

                this.resetForm();
                this.editingId = item.Id || 0;
                this.form = {
                    classId: resp.ClassId || item.ClassId || 0,
                    optionId: resp.OptionId || item.OptionId || 0,
                    armName:
                        resp.SectionName ||
                        resp.SchArm ||
                        item.SectionName ||
                        item.SchArm ||
                        ""
                };
                this.formVisible = true;
                if (this.form.classId) {
                    this.loadFormOptions(this.form.optionId);
                }
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load this arm record."),
                    ""
                );
            } finally {
                this.onloader = "none";
            }
        },
        closeForm() {
            this.formVisible = false;
            this.resetForm();
        },
        handleFormClosed() {
            this.resetForm();
        },
        handleKeydown(event) {
            if (event.key !== "Escape") return;
            if (!this.formVisible || this.isSaving) return;
            this.closeForm();
        },
        clearError(field) {
            if (!this.errors[field]) return;
            const errors = { ...this.errors };
            delete errors[field];
            this.errors = errors;
        },
        validate() {
            const errors = {};
            const classId = this.form.classId;
            if (
                classId === 0 ||
                classId === "0" ||
                classId === "" ||
                classId === null ||
                classId === undefined
            ) {
                errors.classId = "Choose the class for this arm.";
            }

            const optionId = this.form.optionId;
            if (
                optionId === 0 ||
                optionId === "0" ||
                optionId === "" ||
                optionId === null ||
                optionId === undefined
            ) {
                errors.optionId = "Choose the option this arm belongs to.";
            }

            const armName = String(this.form.armName || "").trim();
            if (!armName) {
                errors.armName = "Enter the arm name.";
            }

            this.errors = errors;

            if (Object.keys(errors).length) {
                const fieldIds = {
                    classId: "arm-class",
                    optionId: "arm-option",
                    armName: "arm-name"
                };
                const firstField = Object.keys(errors)[0];
                const element = document.getElementById(fieldIds[firstField]);
                if (element) element.focus();
            }

            return Object.keys(errors).length === 0;
        },
        async submitArm() {
            if (this.isSaving) return;
            if (this.formOptionsLoading) return;
            if (!this.formOptions.length) return;
            if (!this.validate()) return;

            const isEdit = Boolean(this.editingId);
            const savedClass = this.form.classId;
            const savedOption = this.form.optionId;
            const payload = {
                Id: this.editingId || 1,
                ClassId: savedClass,
                OptionId: savedOption,
                SchArm: String(this.form.armName || "").trim(),
                processop: isEdit ? "U" : "N",
                username: this.$store.state.auth.username,
                accesskey: this.$store.state.auth.accesskey
            };

            this.isSaving = true;
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("armregistration", payload);

                if (resp && resp.responseCode && resp.responseCode !== "00") {
                    this.ShowAlertError(
                        resp.responseMessage || "The arm could not be saved.",
                        ""
                    );
                    return;
                }

                this.closeForm();
                this.searchval = "";
                this.selectedClassId = savedClass;
                this.selectedOptionId = savedOption;
                this.optionitems = null;
                this.armitems = null;
                await this.loadOptions({ silent: true });

                if (this.optionsFailed || this.armsFailed) {
                    this.ShowAlertWarning(
                        "Arm saved, but the updated list could not be loaded.",
                        ""
                    );
                } else {
                    this.ShowAlertSuccess(
                        isEdit ? "Arm updated successfully." : "Arm created successfully.",
                        ""
                    );
                }
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to save this arm at the moment."),
                    ""
                );
            } finally {
                this.isSaving = false;
                this.onloader = "none";
            }
        }
    },
    mounted() {
        const userDetails = this.$store.getters["auth/_user"];
        if (!userDetails || userDetails.isSchoolStaff == false) {
            this.$store.dispatch("auth/Logout");
            this.$router.push("/login");
            return;
        }

        document.addEventListener("keydown", this.handleKeydown);
        this.loadClasses();
    },
    beforeDestroy() {
        document.removeEventListener("keydown", this.handleKeydown);
    }
};
</script>

<style scoped>
.arms-page {
    min-height: 100%;
}

.arms-page__body {
    padding-bottom: 2.5rem;
}

.arms-panel {
    position: relative;
    overflow: hidden;
    border: 1px solid #e5eaf0;
    border-radius: 0.9rem;
    background: #fff;
    box-shadow: 0 0.6rem 1.75rem rgba(50, 70, 90, 0.1);
}

.arms-panel__intro {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1.25rem;
    padding: 1.5rem 1.5rem 1.35rem;
    border-bottom: 1px solid #edf0f3;
}

.arms-panel__text {
    min-width: 0;
}

.arms-eyebrow {
    display: block;
    margin-bottom: 0.35rem;
    color: #5b86b5;
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.11em;
}

.arms-panel__intro h1 {
    margin: 0;
    color: #293b52;
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 1.2;
}

.arms-panel__intro p {
    max-width: 48rem;
    margin: 0.45rem 0 0;
    color: #7d8b9a;
    font-size: 0.88rem;
    line-height: 1.55;
}

.arms-panel__side {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.75rem;
}

.arms-stat {
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
    padding: 0.5rem 0.85rem;
    border: 1px solid #dbe7f4;
    border-radius: 0.65rem;
    background: #f5f9ff;
    color: #5b7fa6;
    font-size: 0.76rem;
    font-weight: 600;
    white-space: nowrap;
}

.arms-stat strong {
    color: #1850b8;
    font-size: 1rem;
    font-weight: 700;
}

.arms-add {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    padding: 0.62rem 1.05rem;
    border: 1px solid #1850b8;
    border-radius: 0.55rem;
    background: #1850b8;
    color: #fff;
    font-size: 0.82rem;
    font-weight: 600;
    line-height: 1.2;
    box-shadow: 0 0.35rem 0.9rem rgba(24, 80, 184, 0.22);
    transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.arms-add:hover:not(:disabled),
.arms-add:focus:not(:disabled) {
    background: #144a9e;
    border-color: #144a9e;
    color: #fff;
    box-shadow: 0 0.45rem 1.1rem rgba(24, 80, 184, 0.28);
}

.arms-add:focus-visible {
    outline: 2px solid #1850b8;
    outline-offset: 2px;
}

.arms-add:disabled {
    border-color: #b9c8dc;
    background: #b9c8dc;
    box-shadow: none;
    cursor: not-allowed;
}

.arms-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.85rem 1rem;
    padding: 1.15rem 1.5rem;
    border-bottom: 1px solid #edf0f3;
}

.arms-toolbar h2 {
    margin: 0;
    color: #344767;
    font-size: 1.05rem;
    font-weight: 700;
}

.arms-toolbar__meta {
    margin: 0.3rem 0 0;
    color: #8490a0;
    font-size: 0.76rem;
}

.arms-toolbar__tools {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.65rem;
}

.arms-scope {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    margin: 0;
    color: #7b8998;
    font-size: 0.76rem;
    font-weight: 600;
    white-space: nowrap;
}

.arms-scope__select {
    height: 2.4rem;
    min-width: 150px;
    padding: 0 1.65rem 0 0.6rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.5rem;
    background: #fff;
    color: #344767;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
}

.arms-scope__select:focus {
    border-color: #7ea8dd;
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(24, 80, 184, 0.12);
}

.arms-scope__select:disabled {
    background: #f4f6f9;
    color: #8492a2;
    cursor: not-allowed;
}

.arms-search {
    position: relative;
    display: flex;
    flex: 0 1 220px;
    align-items: center;
    min-width: 190px;
}

.arms-search > i {
    position: absolute;
    left: 0.8rem;
    color: #9aa6b3;
    font-size: 0.8rem;
    pointer-events: none;
}

.arms-search__input {
    width: 100%;
    height: 2.4rem;
    padding: 0.4rem 2.25rem 0.4rem 2.15rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.55rem;
    background: #fff;
    color: #344767;
    font-size: 0.82rem;
    -webkit-appearance: none;
}

.arms-search__input::placeholder {
    color: #a2adba;
}

.arms-search__input::-webkit-search-cancel-button {
    display: none;
}

.arms-search__input:focus {
    border-color: #7ea8dd;
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(24, 80, 184, 0.12);
}

.arms-search__input:disabled {
    background: #f4f6f9;
    color: #a2adba;
    cursor: not-allowed;
}

.arms-search__clear {
    position: absolute;
    right: 0.4rem;
    display: inline-flex;
    width: 1.75rem;
    height: 1.75rem;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #9aa6b3;
    font-size: 0.72rem;
    cursor: pointer;
}

.arms-search__clear:hover,
.arms-search__clear:focus {
    background: #eef2f6;
    color: #526273;
    outline: none;
}

.arms-state {
    display: flex;
    min-height: 240px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    padding: 2rem 1.5rem;
    color: #83909e;
    text-align: center;
}

.arms-state > i {
    margin-bottom: 0.35rem;
    color: #9db6d4;
    font-size: 1.6rem;
}

.arms-state strong {
    color: #526273;
    font-size: 0.95rem;
}

.arms-state span {
    max-width: 28rem;
    font-size: 0.8rem;
    line-height: 1.55;
}

.arms-state .arms-ghost-button {
    margin-top: 0.75rem;
}

.arms-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 0.85rem;
    padding: 1.25rem 1.5rem;
    transition: opacity 0.15s ease;
}

.arms-grid.is-loading {
    opacity: 0.5;
    pointer-events: none;
}

.arm-card {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.8rem;
    padding: 0.85rem 0.9rem;
    border: 1px solid #e5eaf0;
    border-radius: 0.75rem;
    background: #fff;
    box-shadow: 0 0.25rem 0.75rem rgba(50, 70, 90, 0.05);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.arm-card:hover {
    border-color: #bcd4ef;
    box-shadow: 0 0.4rem 1rem rgba(24, 80, 184, 0.1);
}

.arm-card__index {
    display: grid;
    flex: 0 0 2.2rem;
    width: 2.2rem;
    height: 2.2rem;
    place-items: center;
    border-radius: 0.55rem;
    background: #eef4ff;
    color: #2d6fae;
    font-size: 0.68rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.arm-card__name {
    min-width: 0;
    flex: 1 1 auto;
    overflow-wrap: anywhere;
    color: #344767;
    font-size: 0.92rem;
    font-weight: 700;
}

.arm-card__edit {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.35rem;
    padding: 0.32rem 0.55rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.45rem;
    background: #fff;
    color: #44566c;
    font-size: 0.73rem;
    font-weight: 600;
    cursor: pointer;
    transition: border-color 0.15s ease, color 0.15s ease, background-color 0.15s ease;
}

.arm-card__edit:hover,
.arm-card__edit:focus {
    border-color: #9dbfe6;
    background: #f5f9ff;
    color: #1850b8;
    outline: none;
}

.arms-ghost-button {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    margin: 0;
    padding: 0.4rem 0.7rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.45rem;
    background: #fff;
    color: #44566c;
    font-size: 0.76rem;
    font-weight: 600;
    line-height: 1.3;
    transition: border-color 0.15s ease, color 0.15s ease, background-color 0.15s ease;
}

.arms-ghost-button:hover,
.arms-ghost-button:focus {
    border-color: #9dbfe6;
    background: #f5f9ff;
    color: #1850b8;
    outline: none;
}

/* ---------- Drawer form ---------- */

.arms-drawer {
    display: flex;
    min-height: 100%;
    flex-direction: column;
}

.arms-drawer__head {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
    padding-bottom: 1.1rem;
    border-bottom: 1px solid #edf0f3;
}

.arms-drawer__icon {
    display: grid;
    flex: 0 0 2.6rem;
    width: 2.6rem;
    height: 2.6rem;
    place-items: center;
    border-radius: 0.7rem;
    background: #eef4ff;
    color: #1850b8;
    font-size: 1rem;
}

.arms-drawer__heading {
    min-width: 0;
}

.arms-drawer__eyebrow {
    display: block;
    margin-bottom: 0.15rem;
    color: #5b86b5;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.1em;
}

.arms-drawer__heading h2 {
    margin: 0;
    color: #344767;
    font-size: 1.15rem;
    font-weight: 700;
    line-height: 1.25;
}

.arms-drawer__heading p {
    margin: 0.3rem 0 0;
    color: #7d8b9a;
    font-size: 0.8rem;
    line-height: 1.45;
}

.arms-drawer__close {
    display: inline-flex;
    flex: 0 0 2rem;
    width: 2rem;
    height: 2rem;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #8492a2;
    font-size: 0.85rem;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
}

.arms-drawer__close:hover,
.arms-drawer__close:focus {
    background: #eef2f6;
    color: #344767;
    outline: none;
}

.arms-form {
    margin: 0;
}

.arms-form .row {
    margin-right: -7.5px;
    margin-left: -7.5px;
}

.arms-form .row > [class*="col-"] {
    padding-right: 7.5px;
    padding-left: 7.5px;
}

.arms-fieldset {
    margin: 1.35rem 0 0;
    padding: 0;
    border: 0;
}

.arms-fieldset legend {
    display: block;
    width: auto;
    margin: 0 0 0.85rem;
    padding: 0;
    color: #5b86b5;
    font-size: 0.64rem;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
}

.arms-form .form-group {
    margin-bottom: 1rem;
}

.arms-form label {
    margin-bottom: 0.35rem;
    color: #44566c;
    font-size: 0.78rem;
    font-weight: 700;
}

.arms-required {
    color: #c23b4a;
    font-weight: 700;
}

.arms-form .form-control {
    height: 2.5rem;
    padding: 0.35rem 0.7rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.5rem;
    background: #fff;
    color: #344767;
    font-size: 0.84rem;
}

.arms-form .form-control:focus {
    border-color: #7ea8dd;
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(24, 80, 184, 0.12);
}

.arms-form .form-control:disabled {
    background: #f4f6f9;
    color: #8492a2;
    cursor: not-allowed;
}

.arms-form .form-control.is-invalid {
    border-color: #d76a76;
    box-shadow: 0 0 0 0.2rem rgba(194, 59, 74, 0.1);
}

.arms-error {
    display: block;
    margin-top: 0.3rem;
    color: #c23b4a;
    font-size: 0.72rem;
    font-weight: 600;
}

.arms-inline-button {
    margin-left: 0.25rem;
    padding: 0;
    border: 0;
    background: transparent;
    color: #1850b8;
    font-size: 0.72rem;
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
}

.arms-inline-button:hover,
.arms-inline-button:focus {
    color: #144a9e;
    outline: none;
}

.arms-hint {
    display: block;
    margin-top: 0.3rem;
    color: #8a97a6;
    font-size: 0.72rem;
    line-height: 1.45;
}

.arms-notice {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    margin-top: 0.35rem;
    padding: 0.7rem 0.8rem;
    border: 1px solid #f0d9aa;
    border-radius: 0.6rem;
    background: #fff8e9;
    color: #77551d;
    font-size: 0.76rem;
    line-height: 1.5;
}

.arms-notice a {
    color: #1850b8;
    font-weight: 700;
    text-decoration: underline;
}

.arms-drawer__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
    width: 100%;
}

.arms-drawer__actions .btn {
    display: inline-flex;
    min-height: 2.5rem;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    margin: 0;
    padding: 0.4rem 1.1rem;
    border-radius: 0.5rem;
    font-size: 0.82rem;
    font-weight: 600;
}

.arms-submit {
    border: 1px solid #1850b8;
    background: #1850b8;
    color: #fff;
}

.arms-submit:hover,
.arms-submit:focus {
    border-color: #144a9e;
    background: #144a9e;
    color: #fff;
}

.arms-submit:disabled {
    border-color: #b9c8dc;
    background: #b9c8dc;
    color: #fff;
    cursor: not-allowed;
}

@media (max-width: 767.98px) {
    .arms-panel__intro {
        align-items: flex-start;
    }

    .arms-toolbar {
        flex-direction: column;
        align-items: stretch;
    }

    .arms-toolbar__tools {
        gap: 0.55rem;
    }

    .arms-search {
        flex: 1 1 100%;
        width: 100%;
    }
}

@media (max-width: 575.98px) {
    .arms-page__body {
        padding-bottom: 1.5rem;
    }

    .arms-panel__intro {
        flex-direction: column;
        gap: 1rem;
        padding: 1.15rem 1rem 1rem;
    }

    .arms-panel__intro h1 {
        font-size: 1.35rem;
    }

    .arms-panel__side {
        width: 100%;
        flex-wrap: wrap;
    }

    .arms-stat {
        flex: 1 1 auto;
    }

    .arms-add {
        flex: 1 1 auto;
        justify-content: center;
    }

    .arms-toolbar {
        padding: 1rem;
    }

    .arms-toolbar__tools {
        align-items: stretch;
        flex-direction: column;
    }

    .arms-scope {
        justify-content: space-between;
    }

    .arms-scope__select {
        flex: 0 0 auto;
        min-width: 0;
        max-width: 62%;
    }

    .arms-search {
        flex: 0 0 auto;
        min-width: 0;
    }

    .arms-grid {
        padding: 0.85rem;
        grid-template-columns: minmax(0, 1fr);
    }

    .arms-state {
        padding: 1.5rem 1rem;
    }

    .arms-drawer__head {
        gap: 0.7rem;
    }

    .arms-drawer__actions {
        flex-direction: column-reverse;
    }

    .arms-drawer__actions .btn {
        flex: 1 1 auto;
        width: 100%;
    }
}
</style>
